import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import RouteMap from "@/components/RouteMap";
import api from "@/lib/api";
import { ArrowLeft, MapPin, MessageSquare } from "lucide-react";

const STATUS_PILL = {
  Submitted: "as-pill-blue", "Under Review": "as-pill-blue",
  "In Planning": "as-pill-orange", "Awaiting Client Confirmation": "as-pill-orange",
  Confirmed: "as-pill-gold", "In Progress": "as-pill-gold",
  Completed: "as-pill-green", Cancelled: "as-pill-red", Draft: "as-pill-muted",
};

export default function PortalTripDetail() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);

  useEffect(() => { api.get(`/trips/${id}`).then(r => setTrip(r.data)); }, [id]);

  if (!trip) return <DashboardLayout role="client"><div className="p-10 text-[#756E64]">Loading…</div></DashboardLayout>;

  const leg = trip.legs?.[0] || {};
  return (
    <DashboardLayout role="client">
      <div className="max-w-[1300px] mx-auto px-8 py-10">
        <Link to="/portal/trips" className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] hover:text-[#1A1D24] flex items-center gap-2" data-testid="trip-back"><ArrowLeft size={13}/> All missions</Link>

        <div className="mt-6 flex items-start justify-between gap-8">
          <div>
            <div className={`as-pill ${STATUS_PILL[trip.status]}`}>{trip.status}</div>
            <h1 className="as-serif text-6xl md:text-7xl font-light mt-4 leading-none" data-testid="trip-route">
              {leg.departure_airport} <span className="as-gold">→</span> {leg.arrival_airport}
            </h1>
            <div className="text-[#756E64] mt-3 as-mono text-sm">{new Date(leg.departure_datetime).toUTCString()}</div>
          </div>
          <div className="text-right">
            <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Ops Owner</div>
            <div className="as-serif text-2xl mt-1">{trip.owner_name || "Assigning…"}</div>
            <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mt-4">Aircraft</div>
            <div className="as-serif text-xl mt-1">{trip.aircraft_preference || "TBD"} {trip.aircraft_tail && <span className="as-gold as-mono text-sm ml-2">{trip.aircraft_tail}</span>}</div>
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          {/* Live Tracker */}
          <div className="md:col-span-8" data-testid="live-tracker-panel">
            <RouteMap
              departureCode={leg.departure_airport}
              arrivalCode={leg.arrival_airport}
              tail={trip.aircraft_tail}
            />
          </div>

          {/* Timeline */}
          <div className="md:col-span-4 as-card p-5" data-testid="trip-timeline">
            <div className="as-overline mb-4">Timeline</div>
            <ul className="space-y-4">
              {trip.timeline?.slice().reverse().map((t, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-1 bg-[#A47E2A] shrink-0"/>
                  <div>
                    <div className="text-sm text-[#1A1D24]">{t.message}</div>
                    <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-1">{new Date(t.at).toUTCString().slice(0,22)} · {t.actor}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Legs */}
          <div className="md:col-span-8 as-card p-6">
            <div className="as-overline mb-4">Itinerary</div>
            <div className="space-y-3">
              {trip.legs?.map((l, i) => (
                <div key={i} className="flex items-center gap-6 py-3 border-b border-[#E5DFD2] last:border-0">
                  <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] w-16">Leg {i+1}</div>
                  <MapPin size={14} className="as-gold"/>
                  <div className="as-serif text-2xl font-light flex-1">{l.departure_airport} <span className="as-gold">→</span> {l.arrival_airport}</div>
                  <div className="as-mono text-xs text-[#756E64]">{new Date(l.departure_datetime).toUTCString().slice(0,22)}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <Info label="Passengers" value={trip.passenger_count}/>
              <Info label="Luggage" value={trip.luggage_notes || "—"}/>
              <Info label="Special Requirements" value={trip.special_requirements || "—"} full/>
            </div>
          </div>

          {/* Notes to client */}
          <div className="md:col-span-4 as-card p-6" data-testid="client-notes">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={14} className="as-gold"/>
              <div className="as-overline">Updates from Ops</div>
            </div>
            {trip.client_notes?.length ? (
              <ul className="space-y-4">
                {trip.client_notes.slice().reverse().map((n, i) => (
                  <li key={i} className="border-l-2 border-[#A47E2A] pl-4">
                    <div className="text-sm">{n.message}</div>
                    <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-1">{n.actor}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-[#756E64]">No updates yet.</div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Info({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{label}</div>
      <div className="text-sm text-[#1A1D24] mt-1">{value}</div>
    </div>
  );
}
